/**
 * TruckPerformancePanel Component
 * 
 * Dashboard panel showing per-truck revenue performance against targets.
 * Only visible in Testing Mode.
 * 
 * Features:
 * - Weekly target progress
 * - Booked vs Delivered revenue
 * - Revenue gap indicator
 * - On track / At risk / Critical status
 */

import { useState, useEffect, useCallback } from 'react';
import { 
    TrendingUp, 
    TrendingDown,
    Target,
    DollarSign,
    Truck as TruckIcon,
    AlertTriangle,
    CheckCircle2,
    AlertCircle,
    RefreshCw,
    Loader2,
    ChevronRight,
    Calendar,
    Beaker
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status indicator component
function StatusIndicator({ status, statusLabel }) {
    const config = {
        on_track: { 
            icon: CheckCircle2, 
            bg: 'bg-green-500/20', 
            text: 'text-green-400',
            border: 'border-green-500/30'
        },
        at_risk: { 
            icon: AlertCircle, 
            bg: 'bg-yellow-500/20', 
            text: 'text-yellow-400',
            border: 'border-yellow-500/30'
        },
        critical: { 
            icon: AlertTriangle, 
            bg: 'bg-red-500/20', 
            text: 'text-red-400',
            border: 'border-red-500/30'
        }
    };

    const cfg = config[status] || config.at_risk;
    const Icon = cfg.icon;

    return (
        <Badge className={`${cfg.bg} ${cfg.text} ${cfg.border} border`}>
            <Icon className="w-3 h-3 mr-1" />
            {statusLabel}
        </Badge>
    );
}

// Individual truck performance card
function TruckPerformanceCard({ truck, onRefresh }) {
    const progressPercent = Math.min(100, truck.target_percentage || 0);
    const pace = truck.pace || {};
    const homeAlignment = truck.home_alignment || {};
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-all">
            <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                            <TruckIcon className="w-4 h-4 text-orange-400" />
                        </div>
                        <div>
                            <h4 className="text-white font-medium text-sm">{truck.truck_name}</h4>
                            <p className="text-zinc-500 text-xs">{truck.truck_id}</p>
                        </div>
                    </div>
                    <StatusIndicator status={truck.status} statusLabel={truck.status_label} />
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-500">Weekly Progress</span>
                        <span className="text-zinc-400">{truck.target_percentage}%</span>
                    </div>
                    <Progress 
                        value={progressPercent} 
                        className="h-2 bg-zinc-800"
                    />
                </div>

                {/* Revenue metrics */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                        <p className="text-zinc-500 text-xs">Projected</p>
                        <p className="text-white font-semibold">
                            ${truck.projected_revenue?.toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Target</p>
                        <p className="text-zinc-300">
                            ${truck.weekly_target?.toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Gap</p>
                        <p className={`font-medium ${truck.revenue_gap > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {truck.revenue_gap > 0 ? '-' : ''}${truck.revenue_gap?.toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <p className="text-zinc-500 text-xs">Daily Avg</p>
                        <p className="text-zinc-300">
                            ${truck.daily_average?.toLocaleString()}/day
                        </p>
                    </div>
                </div>

                {/* Pace & Home Alignment Intelligence */}
                <div className="border-t border-zinc-800 pt-3 space-y-2">
                    {/* Pace Indicator */}
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-zinc-500 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Pace
                        </span>
                        <div className="flex items-center gap-2">
                            <span className={`font-medium ${
                                pace.pace_status === 'on_pace' ? 'text-green-400' :
                                pace.pace_status === 'at_risk' ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                                {pace.pace_label || 'N/A'}
                            </span>
                            {pace.required_daily_to_target > 0 && (
                                <span className="text-zinc-500">
                                    (Need ${pace.required_daily_to_target?.toLocaleString()}/day)
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Home Alignment Indicator */}
                    {homeAlignment.days_out_target && (
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Home ({homeAlignment.days_out_target}d target)
                            </span>
                            <div className="flex items-center gap-2">
                                <span className={`font-medium ${
                                    homeAlignment.alignment_status === 'on_schedule' ? 'text-green-400' :
                                    homeAlignment.alignment_status === 'risk_of_delay' ? 'text-yellow-400' : 'text-red-400'
                                }`}>
                                    {homeAlignment.alignment_label || 'N/A'}
                                </span>
                                {homeAlignment.projected_days_out > 0 && (
                                    <span className="text-zinc-500">
                                        ({homeAlignment.projected_days_out}d out)
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Week Info */}
                    <div className="flex items-center justify-between text-xs text-zinc-500">
                        <span>Day {truck.days_elapsed}/7</span>
                        <span>{truck.days_remaining} days remaining</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Main panel component
export default function TruckPerformancePanel({ systemMode }) {
    const [loading, setLoading] = useState(false);
    const [performance, setPerformance] = useState(null);
    const [error, setError] = useState(null);

    const fetchPerformance = useCallback(async () => {
        if (systemMode !== 'testing') return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_URL}/api/performance/trucks-summary`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setPerformance(data);
            } else if (response.status === 403) {
                // Not in testing mode
                setPerformance(null);
            } else {
                const err = await response.json();
                setError(err.detail || 'Failed to load performance data');
            }
        } catch (err) {
            console.error('Failed to fetch performance:', err);
            setError('Failed to load performance data');
        } finally {
            setLoading(false);
        }
    }, [systemMode]);

    useEffect(() => {
        fetchPerformance();
    }, [fetchPerformance]);

    // Don't render if not in testing mode
    if (systemMode !== 'testing') {
        return null;
    }

    if (loading && !performance) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <p className="text-zinc-400 text-sm">{error}</p>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={fetchPerformance}
                        className="mt-2 text-orange-400"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!performance || performance.total_trucks === 0) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-orange-400" />
                        <CardTitle className="text-white text-lg">Revenue Performance</CardTitle>
                        <Badge variant="outline" className="border-red-600 text-red-400 text-xs ml-auto">
                            <Beaker className="w-3 h-3 mr-1" />
                            Testing Mode
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="text-center py-6">
                    <TruckIcon className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">No trucks found</p>
                    <p className="text-zinc-500 text-xs mt-1">
                        Create truck profiles to track revenue performance
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="truck-performance-panel">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-orange-400" />
                        <CardTitle className="text-white text-lg">Revenue Performance</CardTitle>
                        <Badge variant="outline" className="border-red-600 text-red-400 text-xs">
                            <Beaker className="w-3 h-3 mr-1" />
                            Testing Mode
                        </Badge>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchPerformance}
                        disabled={loading}
                        className="text-zinc-400 hover:text-white"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-3 pb-3 border-b border-zinc-800">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-green-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-lg font-bold">{performance.on_track}</span>
                        </div>
                        <p className="text-zinc-500 text-xs">On Track</p>
                    </div>
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-yellow-400">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-lg font-bold">{performance.at_risk}</span>
                        </div>
                        <p className="text-zinc-500 text-xs">At Risk</p>
                    </div>
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-red-400">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-lg font-bold">{performance.critical}</span>
                        </div>
                        <p className="text-zinc-500 text-xs">Critical</p>
                    </div>
                </div>

                {/* Truck cards */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {performance.trucks.map(truck => (
                        <TruckPerformanceCard 
                            key={truck.truck_id} 
                            truck={truck}
                            onRefresh={fetchPerformance}
                        />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * Compact version for inline display in other pages
 */
export function TruckPerformanceBadge({ truckId, systemMode }) {
    const [performance, setPerformance] = useState(null);

    useEffect(() => {
        if (systemMode !== 'testing' || !truckId) return;

        const fetchPerformance = async () => {
            try {
                const response = await fetch(`${API_URL}/api/performance/truck/${truckId}`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    setPerformance(data);
                }
            } catch (err) {
                console.error('Failed to fetch truck performance:', err);
            }
        };

        fetchPerformance();
    }, [truckId, systemMode]);

    if (systemMode !== 'testing' || !performance) {
        return null;
    }

    return (
        <div className="flex items-center gap-2 text-xs">
            <StatusIndicator status={performance.status} statusLabel={performance.status_label} />
            <span className="text-zinc-500">
                ${performance.projected_weekly_revenue?.toLocaleString()} / ${performance.weekly_target?.toLocaleString()}
            </span>
        </div>
    );
}
